import { useState } from 'react'
import { styled } from '@mui/material/styles'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import { PickerColumn, PickerContainer } from './picker-column.component'
import { UInput } from './UInput.component'
import type { TextFieldProps } from '@mui/material/TextField'

export interface ModalExpSelectProps {
    value: { month: string; year: string }
    options: { month: string[]; year: string[] }
    onSet: (month: string, year: string) => void
    disabled?: boolean
    error?: boolean
    helperText?: string
    inputTestId?: string
    sx?: TextFieldProps['sx']
}

export const ModalExpSelect = ({ value, options, onSet, disabled, error, helperText, inputTestId, sx }: ModalExpSelectProps) => {
    const { translate } = useTranslate()
    const [open, setOpen] = useState(false)
    const [tempMonth, setTempMonth] = useState(value.month)
    const [tempYear, setTempYear] = useState(value.year)

    const displayValue = value.month && value.year ? `${value.month}/${value.year.slice(2)}` : ''

    const handleOpen = () => {
        if (disabled) return
        setTempMonth(value.month || (options.month[0] ?? ''))
        setTempYear(value.year || (options.year[0] ?? ''))
        setOpen(true)
    }

    const handleCancel = () => {
        setOpen(false)
    }

    const handleOk = () => {
        onSet(tempMonth, tempYear)
        setOpen(false)
    }

    return (
        <>
            <UInput
                value={displayValue}
                placeholder="MM/YY"
                onClick={handleOpen}
                slotProps={{
                    htmlInput: {
                        readOnly: true,
                        style: { cursor: disabled ? 'default' : 'pointer' },
                        ...(inputTestId ? { 'data-testid': inputTestId } : {})
                    }
                }}
                InputLabelProps={{ shrink: false }}
                disabled={disabled}
                error={error}
                helperText={helperText}
                fullWidth
                sx={[
                    {
                        '& .MuiInputBase-input::placeholder': {
                            opacity: 1,
                            color: 'rgba(21, 56, 98, 0.5)',
                            fontSize: 14
                        }
                    },
                    ...(Array.isArray(sx) ? sx : [sx])
                ]}
                data-testid="modal-exp-select-uinput-mm-yy"
            />
            <Dialog
                open={open}
                onClose={handleCancel}
                PaperProps={{
                    sx: {
                        backgroundColor: '#f7f7f7',
                        width: 270,
                        borderRadius: '7px',
                        overflow: 'hidden'
                    }
                }}
                data-testid="modal-exp-select-dialog">
                <DialogContent sx={{ p: 0, overflow: 'hidden' }} data-testid="modal-exp-select-dialog-content">
                    <Stack direction="row" justifyContent="center" sx={{ px: 2, pt: 1.5 }} data-testid="modal-exp-select-stack-header-label-mm">
                        <HeaderLabel data-testid="modal-exp-select-header-label-mm">{translate('mm')}</HeaderLabel>
                        <HeaderLabel data-testid="modal-exp-select-header-label-yyyy">{translate('yyyy')}</HeaderLabel>
                    </Stack>
                    <PickerContainer data-testid="modal-exp-select-picker-container">
                        <PickerColumn
                            options={options.month}
                            value={tempMonth}
                            onChange={setTempMonth}
                            testIdPrefix="modal-exp-select-month"
                            data-testid="modal-exp-select-picker-column-set-temp-month"
                        />
                        <PickerColumn
                            options={options.year}
                            value={tempYear}
                            onChange={setTempYear}
                            testIdPrefix="modal-exp-select-year"
                            data-testid="modal-exp-select-picker-column-set-temp-year"
                        />
                    </PickerContainer>
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'center', borderTop: '1px solid #e3e8ed', p: 0 }} data-testid="modal-exp-select-dialog-actions">
                    <ActionBtn onClick={handleCancel} data-testid="modal-exp-select-action-btn-handle-cancel">
                        {translate('cancel')}
                    </ActionBtn>
                    <ActionBtn onClick={handleOk} sx={{ fontWeight: 'bold' }} data-testid="modal-exp-select-action-btn-handle-ok">
                        {translate('ok')}
                    </ActionBtn>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ModalExpSelect

const HeaderLabel = styled(Typography)`
    flex: 1;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #999;
    text-transform: uppercase;
`

const ActionBtn = styled(Button)`
    flex: 1;
    font-family: 'Poppins', sans-serif;
    color: #22aaec;
    text-transform: none;
    border-radius: 0;
    padding: 10px;

    &:hover {
        background: transparent;
    }
`
